import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { VistaSesion } from '../../../compartido/api';
import { fechaLarga, hhmm, tramos } from '../../../compartido/formato';
import type { Fecha, Hora } from '../../../compartido/tipos';
import { Calendario, type EstadoDia } from '../componentes/Calendario';
import { GrillaHoras } from '../componentes/Horas';
import { Candado, FlechaDer, Reloj } from '../componentes/Iconos';
import { AvisoError, CabeceraPaso, Cambios, Cargando } from '../componentes/Piezas';
import { api, ErrorDeApi, mensajeDeError } from '../lib/api';
import { CORREO_CONTACTO, useConfig } from '../lib/config';
import { hoyEnLima, mesDe, sumarDias, sumarMeses } from '../lib/fechas';
import { useDisponibilidad } from '../lib/ganchos';
import { tokenGuardado } from '../lib/sesion';

const MAX_CAMBIOS = 2;

/**
 * Mover una sesión: otro día, otras horas, la misma cantidad. El horario
 * viejo se libera recién cuando el nuevo queda tomado; si alguien se
 * adelanta, no se pierde nada.
 */
export function Reprogramar() {
  const { id = '' } = useParams();
  const navegar = useNavigate();
  const token = tokenGuardado();
  const { config } = useConfig();
  const [sesion, setSesion] = useState<VistaSesion>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!token) {
      navegar('/acceso', { replace: true });
      return;
    }
    api.misSesiones(token).then(
      (d) => {
        const s = d.sesiones.find((x) => x.id === id);
        if (s) setSesion(s);
        else setError('No encontramos esa sesión entre las tuyas.');
      },
      (e) => setError(mensajeDeError(e)),
    );
  }, [id, token, navegar]);

  if (error) {
    return (
      <div className="pantalla">
        <CabeceraPaso volverA="/mi-sesion" volverTexto="Volver a mis sesiones" titulo="Reprogramar" />
        <div className="contenido">
          <AvisoError>{error}</AvisoError>
        </div>
      </div>
    );
  }
  if (!sesion || !config || !token) return <Cargando />;
  if (!sesion.reprogramable.ok) return <Bloqueado s={sesion} motivo={sesion.reprogramable.motivo} />;

  return (
    <Elegir
      s={sesion}
      token={token}
      casillas={Array.from({ length: config.horaCierre - config.horaApertura }, (_, i) => config.horaApertura + i)}
      ventanaDias={config.ventanaDias}
      diasCerrados={config.diasCerrados}
    />
  );
}

function Elegir({
  s,
  token,
  casillas,
  ventanaDias,
  diasCerrados,
}: {
  s: VistaSesion;
  token: string;
  casillas: Hora[];
  ventanaDias: number;
  diasCerrados: number[];
}) {
  const navegar = useNavigate();
  const n = s.horas.length;
  const hoy = hoyEnLima();
  const ultimo = sumarDias(hoy, ventanaDias);
  const [mes, setMes] = useState(() => mesDe(s.fecha));
  const [fecha, setFecha] = useState<Fecha>();
  const [elegidas, setElegidas] = useState<Hora[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string>();
  const disp = useDisponibilidad(mes, fecha && mesDe(fecha));

  /** Las horas propias cuentan como libres: se puede correr la sesión dentro del mismo día. */
  const libresDe = (f: Fecha): Hora[] => {
    const l = disp.libres(f);
    return f === s.fecha ? [...new Set([...l, ...s.horas])].sort((a, b) => a - b) : l;
  };
  const libres = new Set(fecha ? libresDe(fecha) : []);

  const estado = (f: Fecha): EstadoDia => {
    const l = libresDe(f);
    if (l.length >= n) return 'libre';
    const cerrado = diasCerrados.includes(new Date(`${f}T12:00:00Z`).getUTCDay());
    return !cerrado && f > hoy && f <= ultimo ? 'lleno' : 'no';
  };

  const mismo = fecha === s.fecha && [...elegidas].sort().join() === [...s.horas].sort().join();
  const listo = fecha !== undefined && elegidas.length === n && !mismo;

  async function confirmar() {
    if (!listo || enviando || !fecha) return;
    setEnviando(true);
    setError(undefined);
    try {
      await api.reprogramar(token, s.id, fecha, elegidas);
      navegar('/mi-sesion', { replace: true, state: { aviso: `Listo: tu sesión ahora es el ${fechaLarga(fecha)}, ${tramos(elegidas)}.` } });
    } catch (e) {
      setEnviando(false);
      if (e instanceof ErrorDeApi && e.motivo === 'HORAS_OCUPADAS') {
        disp.recargar();
        setElegidas((x) => x.filter((h) => !e.cuerpo.ocupadas?.includes(h)));
      }
      setError(mensajeDeError(e));
    }
  }

  return (
    <div className="pantalla">
      <CabeceraPaso
        volverA="/mi-sesion"
        volverTexto="Volver a mis sesiones"
        antetitulo={`Cambio ${MAX_CAMBIOS - s.reprogramacionesRestantes + 1} de ${MAX_CAMBIOS}`}
        titulo="Reprogramar"
      />
      <div className="contenido">
        <div className="tarjeta" style={{ flexDirection: 'row', alignItems: 'center', gap: 11 }}>
          <div style={{ flexGrow: 1, flexBasis: 0 }}>
            <div className="antetitulo" style={{ color: 'var(--texto-3)' }}>
              Tu día ahora
            </div>
            <div className="fuerte" style={{ marginTop: 4, fontSize: 14 }}>
              {capital(fechaLarga(s.fecha))}
            </div>
            <div className="nota">{tramos(s.horas)}</div>
          </div>
          <FlechaDer color="#6B5F52" />
          <div style={{ flexGrow: 1, flexBasis: 0 }}>
            <div className="antetitulo">Nuevo</div>
            <div className="fuerte" style={{ marginTop: 4, fontSize: 14 }}>
              {fecha ? capital(fechaLarga(fecha)) : 'Elige un día'}
            </div>
            <div className="nota">{elegidas.length ? elegidas.map(hhmm).join(', ') : `Elige ${n} ${n === 1 ? 'hora' : 'horas'}`}</div>
          </div>
        </div>

        <Calendario
          mes={mes}
          estado={estado}
          elegido={fecha}
          hoy={hoy}
          onElegir={(f) => {
            setFecha(f);
            setElegidas(f === s.fecha ? [...s.horas] : []);
          }}
          onMes={(d) => setMes((m) => sumarMeses(m, d))}
          puedeAtras={mes > mesDe(hoy)}
          puedeAdelante={mes < mesDe(ultimo)}
          cargando={!disp.tiene(mes)}
        />

        {fecha && !disp.tiene(mesDe(fecha)) && <Cargando texto="Buscando horas libres…" />}
        {fecha && disp.tiene(mesDe(fecha)) && (
          <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 9 }}>
            <div className="fila">
              <legend className="campo__etiqueta" style={{ padding: 0, fontSize: 14 }}>
                Nueva hora
              </legend>
              <span className="nota">
                {elegidas.length} de {n} elegidas
              </span>
            </div>
            <GrillaHoras
              casillas={casillas}
              libres={libres}
              elegidas={elegidas}
              maximo={n}
              onAlternar={(h) => setElegidas((e) => (e.includes(h) ? e.filter((x) => x !== h) : [...e, h].sort((a, b) => a - b)))}
            />
            <p className="nota">
              Las tachadas ya las tomó otra persona. Puedes elegirlas seguidas o con huecos
              {elegidas.length === n ? '; para cambiar una, primero apaga otra.' : '.'}
            </p>
          </fieldset>
        )}

        <div className="aviso aviso--calido">
          <Reloj color="#7A5A1E" />
          <p style={{ margin: 0 }}>
            Tu horario actual se libera solo cuando el nuevo quede tomado. Si alguien se adelanta, no pierdes nada.
          </p>
        </div>

        {error && <AvisoError>{error}</AvisoError>}

        <p className="nota">
          Después de este cambio te {s.reprogramacionesRestantes - 1 === 1 ? 'quedará 1' : `quedarán ${s.reprogramacionesRestantes - 1}`} de{' '}
          {MAX_CAMBIOS}. El precio no cambia: {n === 1 ? 'es la misma hora' : `son las mismas ${n} horas`}.
        </p>
      </div>

      <div className="pie">
        <button type="button" className="boton" disabled={!listo || enviando} onClick={confirmar}>
          {enviando ? 'Moviendo tu sesión…' : mismo ? 'Es el mismo horario' : 'Confirmar cambio'}
        </button>
        <Link to="/mi-sesion" className="enlace-texto">
          Dejar todo como está
        </Link>
      </div>
    </div>
  );
}

function Bloqueado({ s, motivo }: { s: VistaSesion; motivo: string }) {
  const horasFaltan = Math.max(0, Math.floor((Date.parse(s.inicioEn) - Date.now()) / 3600_000));
  const [titulo, texto] =
    motivo === 'SIN_CAMBIOS_DISPONIBLES'
      ? ['Ya no quedan cambios', `Usaste los ${MAX_CAMBIOS} cambios que tiene cada sesión.`]
      : motivo === 'DEMASIADO_TARDE'
        ? ['Ya no se puede mover', `Tu sesión empieza en ${horasFaltan} ${horasFaltan === 1 ? 'hora' : 'horas'}. Los cambios se hacen con 24 horas de anticipación como mínimo.`]
        : ['Todavía no se puede mover', 'La sesión se puede mover recién cuando el pago esté confirmado.'];
  return (
    <div className="pantalla">
      <CabeceraPaso volverA="/mi-sesion" volverTexto="Volver a mis sesiones" titulo="Reprogramar" />
      <div className="contenido">
        <div className="aviso aviso--error" style={{ flexDirection: 'column', gap: 12, padding: '20px 18px' }}>
          <div className="icono-redondo" style={{ background: '#F7E2DA', width: 44, height: 44 }}>
            <Candado tam={20} />
          </div>
          <h1 className="titulo" style={{ fontSize: 25, color: 'var(--error)' }}>
            {titulo}
          </h1>
          <p style={{ margin: 0, fontSize: 14 }}>{texto}</p>
        </div>

        <div className="tarjeta">
          <div className="antetitulo" style={{ color: 'var(--texto-3)' }}>
            Tu sesión
          </div>
          <div className="monto" style={{ fontSize: 21 }}>
            {capital(fechaLarga(s.fecha))}
          </div>
          <div className="parrafo">{tramos(s.horas)}</div>
          <hr className="separador" />
          <div className="fila" style={{ alignItems: 'center' }}>
            <span className="parrafo" style={{ fontSize: 13 }}>
              Cambios disponibles
            </span>
            <Cambios restantes={s.reprogramacionesRestantes} total={MAX_CAMBIOS} />
          </div>
        </div>

        {CORREO_CONTACTO && (
          <div className="aviso">
            <Reloj />
            <p style={{ margin: 0 }}>Si tienes un imprevisto, escríbeme. La sesión no tiene devolución, pero podemos ver qué hacer.</p>
          </div>
        )}
      </div>
      <div className="pie">
        <Link to="/mi-sesion" className="boton">
          Volver a mi sesión
        </Link>
        {CORREO_CONTACTO && (
          <a href={`mailto:${CORREO_CONTACTO}`} className="enlace-texto">
            Escribir al mentor
          </a>
        )}
      </div>
    </div>
  );
}

const capital = (x: string) => x[0]!.toUpperCase() + x.slice(1);
